import type { ClientUiPublic } from '../../api/platformConfig'
import type { WorkflowDefinition, WorkflowRun } from '../../api/workflowRuns'
import { comicReferenceCapableModels } from '../comicReferenceModels'
import { platformDisplayLabel } from '../platformDisplayLabel'
import type { WorkflowInputField } from './workflowInputFields'

export const COMIC_PROJECT_INIT_TEMPLATE_PUBLIC_ID = '0180c28e-4f84-7f20-91d5-8f2a00101003'

export const COMIC_STORY_SCALE_OPTIONS = [
  { value: 'short', label: '短篇' },
  { value: 'medium', label: '中篇' },
  { value: 'long', label: '长篇' },
  { value: 'epic', label: '超长篇' },
] as const

export type ComicProjectWorkflowPlan = {
  title?: string
  synopsis?: string
  settings?: Record<string, unknown>
  creative?: Record<string, unknown>
  episodes?: Array<Record<string, unknown>>
}

export function isComicProjectInitDefinition(
  target: Pick<WorkflowDefinition, 'publicId'> | Pick<WorkflowRun, 'definitionPublicId'> | string | null | undefined,
) {
  const publicId = typeof target === 'string'
    ? target
    : (target as { publicId?: string; definitionPublicId?: string } | null | undefined)?.publicId
      ?? (target as { definitionPublicId?: string } | null | undefined)?.definitionPublicId
      ?? ''
  return publicId.trim() === COMIC_PROJECT_INIT_TEMPLATE_PUBLIC_ID
}

export function hydrateComicProjectInitInputFields(
  fields: WorkflowInputField[],
  clientUi: ClientUiPublic,
  values: Record<string, string>,
): WorkflowInputField[] {
  if (fields.length === 0) return fields
  const capableModels = comicReferenceCapableModels(clientUi)
  const platformId =
    values.platformId?.trim()
    || capableModels[0]?.platform
    || clientUi.imageModels[0]?.platform
    || ''
  const platformOptions = Array.from(
    new Map(
      capableModels.map((model) => [
        model.platform,
        {
          value: model.platform,
          label: platformDisplayLabel(model.platform),
        },
      ]),
    ).values(),
  )
  const modelOptions = capableModels
    .filter((model) => model.platform === platformId)
    .map((model) => ({
      value: model.id,
      label: model.label,
      description: model.description,
    }))

  return fields.map((field) => {
    if (field.key === 'storyScale') {
      return { ...field, type: 'select', options: [...COMIC_STORY_SCALE_OPTIONS] }
    }
    if (field.key === 'platformId') {
      return { ...field, type: 'select', options: platformOptions }
    }
    if (field.key === 'modelId') {
      return { ...field, type: 'select', options: modelOptions }
    }
    return field
  })
}

export function normalizeComicProjectInitInputValues(
  fields: WorkflowInputField[],
  values: Record<string, string>,
): Record<string, string> {
  const next = { ...values }
  for (const field of fields) {
    if (field.type !== 'select') continue
    const options = field.options ?? []
    if (options.length === 0) continue
    const current = next[field.key]?.trim() ?? ''
    if (options.some((option) => option.value === current)) continue
    next[field.key] = options[0]!.value
  }
  return next
}

function looksLikePlan(value: unknown): value is ComicProjectWorkflowPlan {
  if (!value || typeof value !== 'object') return false
  const row = value as Record<string, unknown>
  return Array.isArray(row.episodes) || typeof row.title === 'string' || typeof row.settings === 'object'
}

function parsePlanValue(raw: unknown): ComicProjectWorkflowPlan | null {
  if (!raw) return null
  if (looksLikePlan(raw)) return raw
  if (typeof raw !== 'string') return null
  const text = raw.trim()
  if (!text) return null
  try {
    const parsed = JSON.parse(text) as unknown
    return looksLikePlan(parsed) ? parsed : null
  } catch {
    return null
  }
}

export function extractComicProjectWorkflowPlan(run: WorkflowRun | null): ComicProjectWorkflowPlan | null {
  if (!run) return null
  const outputs = [
    ...run.steps.map((step) => step.output).reverse(),
    ...Object.values(run.output ?? {}).reverse().filter(Boolean),
  ]
  for (const output of outputs) {
    if (!output || typeof output !== 'object') continue
    const row = output as Record<string, unknown>
    const direct = parsePlanValue(row.comicProjectPlan)
    if (direct) return direct
    const nestedResult = row.result
    if (nestedResult && typeof nestedResult === 'object') {
      const nested = nestedResult as Record<string, unknown>
      const nestedPlan =
        parsePlanValue(nested.comicProjectPlan)
        ?? parsePlanValue(nested.workflowPlan)
        ?? parsePlanValue(nested.projectPlan)
        ?? parsePlanValue(nested)
      if (nestedPlan) return nestedPlan
    }
  }
  return null
}
